import { defineStore } from 'pinia'
import { ref } from 'vue'
import { isAuthError, handleAuthError } from '@/utils/supabaseErrorHandler'

interface CacheEntry<T> {
  data: T
  timestamp: number
  loading: boolean
}

interface CacheOptions {
  /** Durée de validité du cache en millisecondes */
  ttl?: number
  /** Force le rechargement même si le cache est valide */
  force?: boolean
}

export const useCacheStore = defineStore('cache', () => {
  const cache = ref<Record<string, CacheEntry<any>>>({})

  /**
   * Charge des données avec gestion de cache
   * @param key Clé unique pour identifier les données
   * @param loader Fonction qui charge les données
   * @param options Options de cache
   */
  const loadWithCache = async <T>(
    key: string,
    loader: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> => {
    const entry = cache.value[key]
    const now = Date.now()
    const ttl = options.ttl ?? 5 * 60 * 1000 // 5 minutes par défaut

    // Si on a des données en cache et qu'elles sont encore valides
    if (
      !options.force &&
      entry &&
      now - entry.timestamp < ttl &&
      !entry.loading
    ) {
      return entry.data
    }

    // Si un chargement est déjà en cours, on attend
    if (entry?.loading) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!cache.value[key].loading) {
            clearInterval(interval)
            resolve(cache.value[key].data)
          }
        }, 100)
      })
    }

    // Marquer comme en cours de chargement
    cache.value[key] = {
      data: entry?.data,
      timestamp: entry?.timestamp || 0,
      loading: true
    }

    try {
      const data = await loader()
      cache.value[key] = {
        data,
        timestamp: Date.now(),
        loading: false
      }
      return data
    } catch (error) {
      delete cache.value[key]
      // Intercepter les erreurs 401/JWT et déclencher déconnexion
      if (isAuthError(error)) {
        handleAuthError()
      }
      throw error
    }
  }

  /**
   * Invalide une entrée du cache
   */
  const invalidate = (key: string): void => {
    delete cache.value[key]
  }

  /**
   * Invalide toutes les entrées du cache qui commencent par le préfixe
   */
  const invalidatePrefix = (prefix: string): void => {
    Object.keys(cache.value).forEach((key) => {
      if (key.startsWith(prefix)) {
        delete cache.value[key]
      }
    })
  }

  /**
   * Définit une valeur dans le cache
   * @param key Clé unique pour identifier les données
   * @param data Données à mettre en cache
   */
  const setCache = <T>(key: string, data: T): void => {
    cache.value[key] = {
      data,
      timestamp: Date.now(),
      loading: false
    }
  }

  /**
   * Récupère une valeur du cache
   * @param key Clé unique pour identifier les données
   */
  const getCache = <T>(key: string): T | null => {
    const entry = cache.value[key]
    return entry ? entry.data : null
  }

  /**
   * Nettoie les anciennes clés de cache qui ne correspondent pas à la version actuelle
   * @param currentVersion Version actuelle du cache
   */
  const cleanOldCacheVersions = (currentVersion: string): void => {
    const keys = Object.keys(cache.value)
    keys.forEach(key => {
      // Si la clé ne contient pas la version actuelle, la supprimer
      if (!key.includes(currentVersion)) {
        delete cache.value[key]
      }
    })
  }

  /**
   * Vide complètement le cache
   */
  const clearAll = (): void => {
    cache.value = {}
  }

  /**
   * Vérifie la version du cache et vide tout si elle a changé
   * @param currentVersion Version actuelle du cache
   * @returns true si le cache a été vidé, false sinon
   */
  const checkAndClearOnVersionChange = (currentVersion: string): boolean => {
    const STORAGE_KEY = 'ethicc_cache_version'
    const storedVersion = localStorage.getItem(STORAGE_KEY)
    
    if (storedVersion !== currentVersion) {
      // Vider complètement le cache
      clearAll()
      
      // Mettre à jour la version stockée
      localStorage.setItem(STORAGE_KEY, currentVersion)
      
      return true
    }
    
    return false
  }

  return {
    loadWithCache,
    invalidate,
    invalidatePrefix,
    setCache,
    getCache,
    cleanOldCacheVersions,
    clearAll,
    checkAndClearOnVersionChange
  }
})
